import { useState } from "react";
import type { ExploreResult } from "../lib/api";

/**
 * The Search Console chart: four toggleable metric cards over a shared
 * timeline. Clicks and impressions share the left axis, since both are counts
 * and their relationship matters. CTR and position each take the right axis
 * when selected, one at a time, because rates cannot share a scale with
 * counts and three axes is unreadable.
 */

export type MetricKey = "clicks" | "impressions" | "ctr" | "position";

export const METRICS: {
  key: MetricKey; label: string; color: string; axis: "left" | "right";
  fmt: (v: number | null) => string;
}[] = [
  { key: "clicks", label: "Total clicks", color: "#4c6ef5", axis: "left",
    fmt: (v) => (v == null ? "—" : Math.round(v).toLocaleString()) },
  { key: "impressions", label: "Total impressions", color: "#7048e8", axis: "left",
    fmt: (v) => (v == null ? "—" : Math.round(v).toLocaleString()) },
  { key: "ctr", label: "Average CTR", color: "#0ca678", axis: "right",
    fmt: (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`) },
  { key: "position", label: "Average position", color: "#f08c00", axis: "right",
    fmt: (v) => (v == null ? "—" : v.toFixed(1)) },
];

const W = 960, H = 300;
const M = { top: 18, right: 56, bottom: 34, left: 56 };
const PW = W - M.left - M.right;
const PH = H - M.top - M.bottom;

export function TimeChart({ result, selected, onToggle }: {
  result: ExploreResult;
  selected: MetricKey[];
  onToggle: (k: MetricKey) => void;
}) {
  const cmp = result.compareSeries;
  const [hover, setHover] = useState<number | null>(null);
  const s = result.series;

  const active = METRICS.filter((m) => selected.includes(m.key));
  const leftMetrics = active.filter((m) => m.axis === "left");
  const rightMetric = active.find((m) => m.axis === "right") ?? null;

  // Counts share one zero-based scale so clicks read against impressions.
  const leftMax = Math.max(1, ...leftMetrics.flatMap((m) => [
    ...s.map((p) => Number(p[m.key] ?? 0)),
    ...(cmp ?? []).map((p) => Number(p[m.key] ?? 0)),
  ]));

  const rightVals = rightMetric
    ? [...s, ...(cmp ?? [])]
        .map((p) => p[rightMetric.key]).filter((v): v is number => v != null)
    : [];
  const rightMin = rightMetric?.key === "position" ? Math.min(...rightVals, 1) : 0;
  const rightMax = rightVals.length ? Math.max(...rightVals) : 1;

  const x = (i: number) => M.left + (s.length < 2 ? PW / 2 : (i / (s.length - 1)) * PW);
  const yLeft = (v: number) => M.top + (1 - v / (leftTicksMax(leftMax) || 1)) * PH;
  const yRight = (v: number) => {
    const span = (rightMax - rightMin) || 1;
    const norm = (v - rightMin) / span;
    // Position improves downward, so its axis is inverted: up is better.
    return M.top + (rightMetric?.key === "position" ? norm : 1 - norm) * PH;
  };

  // Round the count axis to a "nice" maximum so ticks land on whole numbers.
  // Fixed fractions of a raw max produce repeats like 3, 2, 2, 1 on small data.
  const leftTicks = niceTicks(leftMax, 5);
  const axisMax = leftTicks[leftTicks.length - 1] || 1;

  const idx = hover ?? s.length - 1;
  const point = s[idx];

  // Rates are undefined on days with no impressions. Drawing them as zero
  // would invent a crash that did not happen, so those days break the line.
  const valueAt = (p: (typeof s)[number], k: MetricKey): number | null => {
    if ((k === "ctr" || k === "position") && !p.impressions) return null;
    return p[k];
  };

  const labelCount = Math.min(7, s.length);
  const dateIdx = Array.from({ length: labelCount }, (_, n) =>
    Math.round((n / Math.max(1, labelCount - 1)) * (s.length - 1)));

  return (
    <div className="tc">
      <div className="tc-cards">
        {METRICS.map((m) => {
          const on = selected.includes(m.key);
          return (
            <button key={m.key}
                    className={`tc-card ${on ? "on" : ""}`}
                    style={on ? { borderTopColor: m.color, background: "#fff" } : undefined}
                    aria-pressed={on}
                    onClick={() => onToggle(m.key)}>
              <span className="tc-card-label">
                <span className="tc-dot" style={{ background: on ? m.color : "#c9c4d8" }} />
                {m.label}
              </span>
              <span className="tc-card-value" style={on ? { color: m.color } : undefined}>
                {m.fmt(hover == null
                  ? result.totals[m.key]
                  : point ? valueAt(point, m.key) : null)}
              </span>
              {result.compareTotals && (() => {
                const now = result.totals[m.key];
                const was = result.compareTotals[m.key];
                if (now == null || was == null || was === 0) return null;
                const delta = (now - was) / Math.abs(was);
                // Position falling is an improvement; everything else rising is.
                const good = m.key === "position" ? delta < 0 : delta > 0;
                return (
                  <span className={`tc-card-delta ${good ? "up" : "down"}`}>
                    {delta > 0 ? "▲" : "▼"} {Math.abs(delta * 100).toFixed(0)}%
                    <em>vs {m.fmt(was)}</em>
                  </span>
                );
              })()}
            </button>
          );
        })}
      </div>

      {s.length === 0 ? (
        <div className="tc-empty">No data in this range.</div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="tc-svg"
             onMouseLeave={() => setHover(null)}
             onMouseMove={(e) => {
               const b = e.currentTarget.getBoundingClientRect();
               const rel = ((e.clientX - b.left) / b.width) * W;
               const i = Math.round(((rel - M.left) / PW) * (s.length - 1));
               setHover(Math.max(0, Math.min(s.length - 1, i)));
             }}>
          {leftTicks.map((tv) => {
            const frac = tv / axisMax;
            const yy = M.top + (1 - frac) * PH;
            const rv = rightMetric
              ? (rightMetric.key === "position"
                  ? rightMin + (1 - frac) * (rightMax - rightMin)
                  : rightMin + frac * (rightMax - rightMin))
              : null;
            return (
              <g key={tv}>
                <line x1={M.left} y1={yy} x2={M.left + PW} y2={yy} stroke="#eeecf4" />
                {leftMetrics.length > 0 && (
                  <text x={M.left - 8} y={yy + 3.5} fontSize="10" textAnchor="end"
                        fill="#8b869e" fontFamily="JetBrains Mono, monospace">
                    {compact(tv)}
                  </text>
                )}
                {rightMetric && rv != null && (
                  <text x={M.left + PW + 8} y={yy + 3.5} fontSize="10"
                        fill={rightMetric.color} fontFamily="JetBrains Mono, monospace">
                    {rightMetric.fmt(rv)}
                  </text>
                )}
              </g>
            );
          })}

          <line x1={M.left} y1={M.top + PH} x2={M.left + PW} y2={M.top + PH} stroke="#ddd9e8" />

          {dateIdx.map((i) => (
            <text key={i} x={x(i)} y={M.top + PH + 17} fontSize="10" textAnchor="middle"
                  fill="#8b869e" fontFamily="JetBrains Mono, monospace">
              {shortDate(s[i].date)}
            </text>
          ))}

          {hover != null && (
            <line x1={x(hover)} y1={M.top} x2={x(hover)} y2={M.top + PH}
                  stroke="#b9a9e8" strokeDasharray="3 3" />
          )}

          {/* Previous period first, so the current period draws over it. */}
          {cmp && active.map((m) => {
            const yf = m.axis === "left" ? yLeft : yRight;
            const cx = (i: number) =>
              M.left + (cmp.length < 2 ? PW / 2 : (i / (cmp.length - 1)) * PW);
            let pen = false;
            const d = cmp.map((p, i) => {
              const v = valueAt(p, m.key);
              if (v == null) { pen = false; return null; }
              const cmd = pen ? "L" : "M";
              pen = true;
              return `${cmd}${cx(i).toFixed(1)},${yf(v).toFixed(1)}`;
            }).filter(Boolean).join(" ");
            return (
              <path key={`cmp-${m.key}`} d={d} fill="none" stroke={m.color}
                    strokeWidth="1.5" strokeDasharray="4 4" opacity="0.45"
                    vectorEffect="non-scaling-stroke" />
            );
          })}

          {active.map((m) => {
            const yf = m.axis === "left" ? yLeft : yRight;
            // Broken lines rather than interpolation across missing days.
            let pen = false;
            const d = s.map((p, i) => {
              const v = valueAt(p, m.key);
              if (v == null) { pen = false; return null; }
              const cmd = pen ? "L" : "M";
              pen = true;
              return `${cmd}${x(i).toFixed(1)},${yf(v).toFixed(1)}`;
            }).filter(Boolean).join(" ");
            return (
              <g key={m.key}>
                <path d={d} fill="none" stroke={m.color} strokeWidth="2"
                      strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                {hover != null && point && valueAt(point, m.key) != null && (
                  <circle cx={x(hover)} cy={yf(valueAt(point, m.key) as number)} r="4"
                          fill="#fff" stroke={m.color} strokeWidth="2" />
                )}
              </g>
            );
          })}

          {hover != null && (
            <text x={x(hover)} y={M.top - 5} fontSize="10" textAnchor="middle"
                  fill="#5b21b6" fontFamily="JetBrains Mono, monospace">
              {s[hover].date}
            </text>
          )}
        </svg>
      )}
    </div>
  );
}

/**
 * Tick values that land on 1, 2, 5 multiples of a power of ten, so a max of 3
 * gives 0,1,2,3 rather than 0,0.75,1.5,2.25,3 rounded into repeats. Small
 * integer maxima step by one.
 */
function niceTicks(max: number, target: number): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  if (max <= target) {
    const n = Math.max(1, Math.ceil(max));
    return Array.from({ length: n + 1 }, (_, i) => i);
  }
  const raw = max / target;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const stepMul = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  const step = stepMul * mag;
  const top = Math.ceil(max / step) * step;
  const out: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) out.push(Number(v.toFixed(6)));
  return out;
}

function leftTicksMax(max: number): number {
  const t = niceTicks(max, 5);
  return t[t.length - 1];
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (n >= 1_000) return `${trim(n / 1_000)}K`;
  return String(Math.round(n));
}

/** 1.0K reads worse than 1K. */
function trim(n: number): string {
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

function shortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}`;
}
